import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../integrations/supabase/client';
import { Category, CategoryType } from '../types/category';
import { useAuth } from './useAuth';

export function useCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchCategories = useCallback(async () => {
    if (!user) {
      setCategories([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('categories')
        .select('*')
        .eq('user_id', user.id)
        .order('name');

      if (fetchError) {
        setError(fetchError.message);
        console.error('Error fetching categories:', fetchError);
        return;
      }

      setCategories(data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro inesperado ao carregar categorias';
      setError(errorMessage);
      console.error('Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const getCategoriesByType = useCallback((type: CategoryType) => {
    return categories.filter(category => category.type === type);
  }, [categories]);

  const getCategoryById = useCallback((id: string | number) => {
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    return categories.find(category => category.id === numericId);
  }, [categories]);

  const createCategory = useCallback(async (categoryData: Omit<Category, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const { data, error } = await supabase
        .from('categories')
        .insert({
          ...categoryData,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Immediate optimistic update (fallback if real-time doesn't work)
      if (data) {
        setCategories(prev => [...prev, data]);
        console.log('➕ Optimistic insert applied:', data);
        
        // Force a refetch after a short delay to ensure data consistency
        setTimeout(() => {
          fetchCategories();
        }, 500);
      }

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao criar categoria';
      throw new Error(errorMessage);
    }
  }, [user]);

  const updateCategory = useCallback(async (id: string | number, updates: Partial<Category>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Convert to number for consistency
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;

    try {
      const { data, error } = await supabase
        .from('categories')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', numericId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Immediate optimistic update (fallback if real-time doesn't work)
      if (data) {
        setCategories(prev => prev.map(cat => cat.id === numericId ? data : cat));
        console.log('✏️ Optimistic update applied:', data);
        
        // Force a refetch after a short delay to ensure data consistency
        setTimeout(() => {
          fetchCategories();
        }, 500);
      }

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar categoria';
      throw new Error(errorMessage);
    }
  }, [user]);

  const deleteCategory = useCallback(async (id: string | number) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Convert to number for consistency
    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;

    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', numericId)
        .eq('user_id', user.id);

      if (error) {
        throw error;
      }

      // Immediate optimistic update (fallback if real-time doesn't work)
      setCategories(prev => {
        const filtered = prev.filter(cat => cat.id !== numericId);
        console.log('🗑️ Optimistic delete - Before:', prev.length, 'After:', filtered.length, 'Removed ID:', numericId);
        return filtered;
      });
      
      // Force a refetch after a short delay to ensure data consistency
      setTimeout(() => {
        fetchCategories();
      }, 500);

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao deletar categoria';
      throw new Error(errorMessage);
    }
  }, [user]);

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return;

    console.log('🔄 Setting up real-time subscription for user:', user.id);

    const subscription = supabase
      .channel(`categories-realtime_${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'categories',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('📡 Real-time event received:', payload.eventType, payload);

          switch (payload.eventType) {
            case 'INSERT':
              console.log('➕ Adding category:', payload.new);
              setCategories(prev => [...prev, payload.new as Category]);
              break;
            case 'UPDATE':
              console.log('✏️ Updating category:', payload.new);
              setCategories(prev => prev.map(cat =>
                cat.id === payload.new.id ? payload.new as Category : cat
              ));
              break;
            case 'DELETE':
              console.log('🗑️ Removing category:', payload.old);
              setCategories(prev => prev.filter(cat => cat.id !== payload.old.id));
              break;
          }
        }
      )
      .subscribe((status) => {
        console.log('📊 Subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Real-time subscription active for categories');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Real-time subscription error');
        }
      });

    return () => {
      console.log('🛑 Cleaning up real-time subscription');
      supabase.removeChannel(subscription);
    };
  }, [user]);

  // Initial fetch
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    loading,
    error,
    refetch: fetchCategories,
    getCategoriesByType,
    getCategoryById,
    createCategory,
    updateCategory,
    deleteCategory,
  };
}